<template>
  <div class="effect-card">
    <div
        v-for="row in rows"
        :key="row.key"
        class="effect-card__row">
      <label class="effect-card__label">{{ row.label }}</label>
      <input
          :value="row.value"
          :min="row.min"
          :max="row.max"
          :step="row.step"
          :aria-label="row.label"
          :style="trackStyle(row.fill)"
          class="effect-card__range"
          type="range"
          @input="$emit('input', { key: row.key, value: $event.target.value })">
      <span class="effect-card__value">{{ row.display }}</span>
    </div>
    <div
        v-if="showColor"
        class="effect-card__row">
      <span class="effect-card__label">{{ colorLabel }}</span>
      <div class="effect-card__color">
        <button
            :style="{ background: color }"
            :aria-label="colorAria"
            type="button"
            class="effect-card__swatch"
            @click.stop="$emit('pick', $event)"/>
        <slot name="picker"/>
        <span class="effect-card__hex">{{ hexText }}</span>
      </div>
    </div>
  </div>
</template>

<script>
function toHexLabel(color) {
  const value = (color || '').trim();
  const hex = value.match(/^#?([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hex) {
    const digits = hex[1].length === 3
      ? hex[1].split('').map(char => char + char).join('')
      : hex[1];
    return digits.toUpperCase();
  }
  const rgba = value.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgba) {
    return rgba.slice(1, 4)
      .map(channel => (`0${Math.max(0, Math.min(255, parseInt(channel, 10))).toString(16)}`).slice(-2))
      .join('')
      .toUpperCase();
  }
  return value;
}

export default {
  name:  'EffectControlCard',
  props: {
    rows:       { type: Array, default: () => [] },
    showColor:  { type: Boolean, default: false },
    color:      { type: String, default: '#000000' },
    colorLabel: { type: String, default: 'Color' },
    colorAria:  { type: String, default: 'Open color picker' },
  },
  computed: {
    hexText() {
      return toHexLabel(this.color);
    },
  },
  methods: {
    trackStyle(fill) {
      const percent = Math.min(100, Math.max(0, Number(fill) || 0));
      return {
        background: `linear-gradient(to right, #1570EF ${percent}%, #D5D9E0 ${percent}%, #D5D9E0 100%)`,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/lp-tokens';

.effect-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-radius: 10px;
  background: #f3f4f6;
}

.effect-card__row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.effect-card__label {
  width: 84px;
  flex-shrink: 0;
  margin: 0;
  font-size: 12.5px;
  color: #3d4350;
}

.effect-card__range {
  flex: 1 1 auto;
  min-width: 0;
  height: 4px;
  margin: 0;
  border-radius: 4px;
  outline: none;
  cursor: pointer;
  -webkit-appearance: none;
  appearance: none;

  &::-webkit-slider-thumb {
    width: 16px;
    height: 16px;
    border: 2px solid #fff;
    border-radius: 50%;
    background: #1570ef;
    box-shadow: 0 1px 3px rgba(20, 22, 27, 0.3);
    cursor: pointer;
    -webkit-appearance: none;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: 2px solid #fff;
    border-radius: 50%;
    background: #1570ef;
    cursor: pointer;
  }
}

.effect-card__value {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 26px;
  border-radius: 6px;
  background: #f3f4f6;
  font-family: $lp-mono;
  font-size: 12px;
  color: #14161b;
}

.effect-card__color {
  display: flex;
  flex: 1 1 0;
  align-items: center;
  gap: 8px;
  min-width: 0;
  height: 32px;
  min-height: 32px;
  padding: 0 9px 0 5px;
  box-sizing: border-box;
  border-radius: 8px;
  background: #f3f4f6;
}

.effect-card__swatch {
  position: relative;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  padding: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  cursor: pointer;
}

.effect-card__hex {
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  padding: 0;
  border: 0;
  outline: none;
  background: transparent;
  font-family: $lp-mono;
  font-size: 12.5px;
  color: #14161b;
  text-transform: uppercase;
  cursor: default;
  user-select: none;
}
</style>
